import type { CatchGrant, PetCard, TreatSlot } from "@highmarch/app";
import type { PetSheet } from "@highmarch/app";
import type { PetData } from "@highmarch/domain";

/**
 * Every word the pets say on screen (docs/VOICE.md: short, verbs first, the numbers kept; Bram first person, gruff and
 * warm): the battle card's chip and treat well, the wild pet's banners and Bram's lines, the catch card, the escape,
 * the end screen's row, the pet sheet's abilities and skins, the toasts. Strings only, so test/petWords.test.ts pins them.
 */

/** The abilities unlocked, in a line ("Bite · Muster · Rally Bark"). */
export function jobLine(c: PetCard): string { return c.unlocked.map((a) => c.pet.abilities[a].name).join(" · ") || c.pet.blurb; }

/** "Lv 6", or "Lv 12 · cap" at the highest tower level (it can go no further until a tower does). */
export function levelWords(c: Pick<PetCard, "level" | "cap">): string { return c.level >= c.cap ? `Lv ${c.level} · cap` : `Lv ${c.level}`; }

/** The level against the cap, 0–1. */
export function levelShare(c: Pick<PetCard, "level" | "cap">): number { return c.cap > 0 ? Math.max(0, Math.min(1, c.level / c.cap)) : 1; }

/** The toast when the chip is tapped: the pet comes along, or stays home. */
export function equipToast(pet: PetData, c: PetCard | null): string {
  return c ? `${pet.name} comes along: ${jobLine(c).toLowerCase()}` : `${pet.name} stays home this time`;
}

/** The battle card's pet picker (Pets v3): its heading and the go-without choice. */
export function pickWords(): { readonly title: string; readonly sub: string; readonly none: string; readonly noneSub: string } {
  return { title: "Who comes along?", sub: "One pet fights at your flag each battle", none: "None", noneSub: "All stay home" };
}

/** An uncaught pet's Towers-tab card (Pets v3): where it first turns up. */
export function roamWords(level: number): string { return `Roams from level ${level}`; }

/** The word a pet shouts when its pulse goes off (the pup's "WOOF!"), by pet id; "!" for one not listed. */
const SHOUTS: Readonly<Record<string, string>> = {
  muster_pup: "WOOF!", lantern_owl: "HOOT!", ember_kit: "YIP!", crystal_tortoise: "HMPH!", bubble_axolotl: "BLUB!", star_magpie: "CAW!",
};
export function petShout(petId: string): string { return SHOUTS[petId] ?? "!"; }

/** The note off the HUD chip: the pup's muster bark "+muster", else the pulse's own name ("Lantern Flare!"). */
export function chipNote(pet: PetData | null, kind: "muster" | "pulse"): string {
  if (kind === "muster") return "+muster";
  return pet ? `${pet.abilities.pulse.name}!` : "";
}

/** The battle card before any pet is caught (`fromLevel`: the first level a wild one roams). */
export function noPetWords(fromLevel: number): { readonly title: string; readonly sub: string } {
  return { title: "Wild pets", sub: `From level ${fromLevel}` };
}

/** "3.5%" (one decimal only when it has one). */
export function pct(n: number): string { return `${Number.isInteger(n) ? n : n.toFixed(1)}%`; }

/**
 * The treat well's lines: the armed treat (or "Pet Treat") and the coat chance a wild pet would roll with it, and what a
 * tap does (`hint`: for the well's label). `base`: the chance with none (%).
 */
export function treatWellWords(slots: readonly TreatSlot[], base: number): { readonly title: string; readonly sub: string; readonly hint: string; readonly empty: boolean } {
  const armed = slots.find((s) => s.armed);
  if (armed) return { title: armed.treat.name, sub: `Rare coat ${pct(armed.skinChancePercent)}`, hint: "Armed for the next battle", empty: false };
  const held = slots.reduce((n, s) => n + s.have, 0);
  return { title: "Pet Treat", sub: `Rare coat ${pct(base)}`, hint: held > 0 ? "Tap to arm one" : "Get some in the Shop", empty: held === 0 };
}

/** The toast on a treat armed or put away. */
export function treatToast(slot: TreatSlot | null, base: number): string {
  return slot ? `${slot.treat.name} armed: coat chance ${pct(slot.skinChancePercent)} next battle` : `Treat put away: coat chance ${pct(base)}`;
}

/** The Shop's Pet Treats shelf: its heading, each tile's line, the confirm's where-it-waits and the toast after a buy. */
export function treatShelfWords(): { readonly title: string; readonly where: string; chance(p: string): string; bought(name: string): string } {
  return {
    title: "Pet Treats",
    where: "It waits on the battle card: arm it there before a battle. It's eaten only when a wild pet turns up.",
    chance: (p) => `Rare coat ${p}`,
    bought: (name) => `+1 ${name}: arm it on the battle card`,
  };
}

/** The wild pet's banner (`skin`: the rare coat's name it wears, or null). */
export function wildBannerWords(pet: PetData, skin: string | null): { readonly title: string; readonly sub: string } {
  return { title: `A wild ${pet.name}!`, sub: skin !== null ? `In a rare coat: ${skin}! Tap it to give chase` : "Tap it if you fancy a chase" };
}

/** The chase's banner as it comes down the road. */
export function chaseWords(pet: PetData): { readonly title: string; readonly sub: string } {
  return { title: `CATCH THE ${pet.name.toUpperCase()}!`, sub: "Wear it down before it reaches the gate" };
}

/**
 * The mid-battle catch card: the title, what it paid, the new coat (null when it wore none new), and Bram's cheer
 * (`skinName`: the coat's name). First catch "Muster Pup caught! It joins your keep." · a level "Muster Pup Lv 7 → 8" ·
 * at the cap "+340 gold" · a coat "New coat: Snowdrift!".
 */
export function catchCardWords(g: CatchGrant, pet: PetData, skinName: string | null): { readonly title: string; readonly sub: string; readonly coat: string | null; readonly bram: string } {
  const r = g.reward, coat = g.skin !== null && g.skinNew && skinName ? `New coat: ${skinName}!` : null;
  if (r.kind === "unlocked") return { title: `${pet.name} caught!`, sub: "It joins your keep.", coat, bram: "Got it! It's ours, Commander. Raise its level on the Towers tab." };
  if (r.kind === "levelUp") return { title: `${pet.name} Lv ${r.from} → ${r.to}`, sub: "Caught again: one level stronger", coat, bram: "Caught again! It's stronger for the chase." };
  return { title: `${pet.name} caught · +${r.gold} gold`, sub: `At Lv ${r.level}, your towers' best: its upgrade's gold instead`, coat, bram: "Caught! It's as strong as our towers allow, so take the gold." };
}

/** The escape note: it reached the gate. */
export function escapeWords(pet: PetData): { readonly title: string; readonly sub: string } {
  return { title: "It got away!", sub: `The ${pet.name} will be back next level` };
}

/** The end screen's row for a catch: title, sub-line and its stamp. */
export function catchWords(g: CatchGrant, pet: PetData): { readonly title: string; readonly sub: string; readonly end: string } {
  const r = g.reward;
  const title = r.kind === "unlocked" ? `${pet.name} caught!` : r.kind === "levelUp" ? `${pet.name} caught · Lv ${r.to}` : `${pet.name} caught · +${r.gold} gold`;
  const sub = g.skin !== null && g.skinNew ? "It wore a rare coat: the skin is yours" : r.kind === "gold" ? "At your towers' level: its next upgrade's gold" : r.kind === "unlocked" ? "It joins your keep" : "One level stronger";
  return { title, sub, end: r.kind === "unlocked" ? "NEW" : r.kind === "levelUp" ? `Lv ${r.to}` : "" };
}

/** The pet's upgrade in a line (the Towers tab's card, the sheet's sub-line). */
export function petStateWords(s: PetSheet): string {
  const u = s.upgrade;
  switch (u.state) {
    case "running": return `Upgrading to Lv ${u.job?.toLevel ?? u.toLevel}`;
    case "queued": return `Queued: Lv ${u.toLevel - 1}`;
    case "ready": return u.affordable ? `Upgrade ready: Lv ${u.toLevel}` : `Next: Lv ${u.toLevel} · ${u.cost.toLocaleString("en-US")} gold`;
    case "cap": return "Raise a tower to go higher";
    case "max": return "Max level";
    case "notOwned": return "Catch a wild one to bring it home";
  }
}

/** The sheet's cap line: why the level stops ("Lv 7 / 12 · your best tower's level"). */
export function capWords(s: Pick<PetSheet, "level" | "cap" | "maxLevel">): string {
  return s.level >= s.maxLevel ? `Lv ${s.level} · max` : `Lv ${s.level} / ${s.cap}`;
}

/** The come-along switch's words. */
export function comeAlongWords(on: boolean): { readonly title: string; readonly sub: string } {
  return on ? { title: "Comes along", sub: "Fights at your rally flag every battle" } : { title: "Stays home", sub: "Tap to bring it into battles" };
}

/** A locked ability's note. */
export function unlockWords(fromLevel: number): string { return `Unlocks at Lv ${fromLevel}`; }

/** A pet skin on the sheet and the Skins tab: worn / owned, or how to get it. */
export function coatWords(owned: boolean, worn: boolean): string { return worn ? "Wearing" : owned ? "Caught" : "Catch a wild one wearing it"; }
